import { Suspense } from "react";

import { logger } from "c7-logger";

import { LoadingSpinnerWrapped } from "chameleon/lib/components";

import { CheckoutExperienceWrapper } from "checkout-experience/layouts/checkout-experience-wrapper";
import { CriticalError } from "checkout-experience/layouts/critical-error";
import { useCheckoutSession } from "checkout-query-client/hooks/use-checkout-session";
import { useMakePayment } from "checkout-query-client/hooks/use-make-payment";
import { useMerchantSettings } from "checkout-query-client/hooks/use-merchant-settings";
import { useAppContext } from "checkout-query-client/providers/app-context-wrapper";

import { InitParams } from "drop-in-ui/components/init-wrapper/types";
import { KOUNT_SESSION_HOSTNAME } from "drop-in-ui/utils/configs";

import { PayResponse, PayResponse_Status } from "frontend-client";

/**
 * The goal of this function is to call the merchant config and provide the required information to the checkout form
 */
export function InitWrapper({
  themeValueOverrides,
  i18nInstance,
  iframePayDomain,
}: Omit<InitParams, "checkoutSessionToken">) {
  const contextValue = useAppContext();
  const {
    checkoutSessionToken,
    apiHost,
    version,
    googlePayEnv,
    pazeSDK,
    deviceFingerprintSDK,
    target,
  } = contextValue;

  const {
    error: merchantSettingsError,
    isLoading,
    data: merchantSettings,
  } = useMerchantSettings({
    checkoutSessionToken,
    apiHost,
    version,
  });
  const {
    isLoading: isLoadingGetCheckoutSession,
    data: checkoutSession,
    error: checkoutSessionError,
  } = useCheckoutSession({ checkoutSessionToken, apiHost, version });
  const {
    mutateAsync: makePayment,
    data: paymentData,
    isPending: isPayInProgress,
  } = useMakePayment(
    checkoutSessionToken,
    apiHost,
    version,
    i18nInstance.language
  );
  if (merchantSettingsError || checkoutSessionError) {
    return (
      <CriticalError error={merchantSettingsError || checkoutSessionError} />
    );
  }

  const onPayResult = (
    result: PayResponse
  ): Promise<PayResponse_Status | void> => {
    logger.log(`On Pay Call completed with status: ${result.status}`);
    return Promise.resolve(result.status);
  };

  return (
    <Suspense fallback={<LoadingSpinnerWrapped />}>
      <CheckoutExperienceWrapper
        themeOverrides={themeValueOverrides}
        isLoading={isLoading || isLoadingGetCheckoutSession}
        isPayInProgress={isPayInProgress}
        paymentError={
          paymentData?.status !== PayResponse_Status.SUCCESS
            ? paymentData?.payRequestErrorCode
            : undefined
        }
        makePayment={makePayment}
        onPayResult={onPayResult}
        merchantSettings={merchantSettings}
        experienceType={"dropInUI"}
        checkoutSession={checkoutSession}
        isCheckoutSessionLoading={isLoadingGetCheckoutSession}
        checkoutSessionToken={checkoutSessionToken}
        apiHost={apiHost}
        version={version}
        kountSessionHostname={KOUNT_SESSION_HOSTNAME}
        googlePayEnv={googlePayEnv}
        pazeSDK={pazeSDK}
        deviceFingerprintSDK={deviceFingerprintSDK}
        controlledSubmit={contextValue.controlledSubmit}
        i18nInstance={i18nInstance}
        target={target}
        iframePayDomain={iframePayDomain}
      />
    </Suspense>
  );
}
